import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { LotterySlug } from "@/hooks/use-lottery-selection";

export type NumberStatLite = {
  number: number;
  frequency: number;
  delay: number | null;
};

/**
 * Hook compartilhado para obter frequência e atraso de cada dezena
 * (histórico completo) — usado pelos quadros de seleção de Combinações
 * e Bolões para enriquecer o tooltip de cada número.
 */
export function useNumberStats(slug: LotterySlug) {
  return useQuery({
    // Não reutilizar a mesma chave da tela de Frequência. Esta query retorna
    // um Map para tooltip; Frequência retorna array para gráficos. Se as duas
    // dividem cache, React Query entrega Map onde a tela espera array e a rota
    // quebra ao navegar de Combinações/Bolões para Frequência.
    queryKey: ["number-stats-tooltip", slug, "all"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_number_stats", {
        _lottery_slug: slug,
        _last_n: null,
      });
      if (error) {
        // Estatísticas no tooltip são enriquecimento visual; não podem quebrar
        // Combinações/Bolões nem apagar escolhas se a sessão oscilar.
        return new Map<number, { frequency: number; delay: number | null }>();
      }
      const rows = (data ?? []) as NumberStatLite[];
      const map = new Map<number, { frequency: number; delay: number | null }>();
      rows.forEach((r) => map.set(r.number, { frequency: r.frequency, delay: r.delay }));
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function statTitle(
  n: number,
  stat: { frequency: number; delay: number | null } | undefined,
): string {
  const label = `Dezena ${String(n).padStart(2, "0")}`;
  if (!stat) return label;
  const freq = `${stat.frequency} vez${stat.frequency === 1 ? "" : "es"}`;
  const delay =
    stat.delay == null
      ? "sem registro"
      : stat.delay === 0
        ? "saiu no último concurso"
        : `${stat.delay} concurso${stat.delay === 1 ? "" : "s"} sem sair`;
  return `${label}\nFrequência: ${freq}\nAtraso: ${delay}`;
}